import { ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type {
  RiskReductionMeasureLinkedHazard,
  SafetyFunctionLinkedRiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Badge } from '@/components/ui/badge'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { hazardDetailLinkStateFromSafetyFunction } from '../hazards/hazardDetailNavigation'

type SafetyFunctionLinkedRiskReductionMeasuresSectionProps = {
  linkedRiskReductionMeasures: SafetyFunctionLinkedRiskReductionMeasure[]
  referenceHazardId: string | null
  safetyFunctionId: string
}

function getHazardTypeLabel(
  type: string | null,
  naLabel: string,
  translate: (key: string) => string
) {
  if (!type) {
    return naLabel
  }
  const key = `hazardsPage.enums.hazardTypes.${type}`
  const translated = translate(key)
  return translated === key ? type : translated
}

function getMeasureTypeLabel(
  type: SafetyFunctionLinkedRiskReductionMeasure['type'],
  translate: (key: string) => string
) {
  return translate(`riskReductionMeasuresLibraryPage.form.measureTypes.${type}`)
}

function LinkedEntityDescription({
  description,
}: {
  description: string
}) {
  return (
    <ItemDescription className='line-clamp-2 text-xs text-muted-foreground'>
      <HoverCard>
        <HoverCardTrigger asChild>
          <span className='block cursor-default'>{description}</span>
        </HoverCardTrigger>
        <HoverCardContent
          side='top'
          className='max-w-sm text-sm text-muted-foreground'
        >
          <p className='whitespace-pre-wrap'>{description}</p>
        </HoverCardContent>
      </HoverCard>
    </ItemDescription>
  )
}

function LinkedHazardRow({
  hazard,
  referenceHazardId,
  safetyFunctionId,
  naLabel,
  translate,
}: {
  hazard: RiskReductionMeasureLinkedHazard
  referenceHazardId: string | null
  safetyFunctionId: string
  naLabel: string
  translate: (key: string, options?: Record<string, string>) => string
}) {
  const hazardLabel = getHazardTypeLabel(hazard.type, naLabel, translate)
  const isReferenceHazard =
    referenceHazardId != null && hazard.id === referenceHazardId
  const hasDescription =
    hazard.description != null && hazard.description.trim().length > 0

  return (
    <li>
      <Item variant='outline' size='sm' className='bg-background'>
        <ItemContent className='min-w-0 flex-1'>
          <ItemTitle className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-foreground'>
            <Badge
              variant='outline'
              className='shrink-0 border-amber-300 bg-amber-50 font-normal text-amber-800'
            >
              {translate('ehsrsOverviewPage.badgeHazards')}
            </Badge>
            <span>{hazardLabel}</span>
            {isReferenceHazard && (
              <Badge variant='outline' className='shrink-0 font-normal'>
                {translate(
                  'safetyFunctionsPage.sfDetail.linkedRiskReductionMeasures.referenceHazardBadge'
                )}
              </Badge>
            )}
          </ItemTitle>
          {hasDescription && (
            <LinkedEntityDescription description={hazard.description?.trim() ?? ''} />
          )}
        </ItemContent>
        <Link
          relative='route'
          to={`../hazards/${hazard.id}`}
          state={hazardDetailLinkStateFromSafetyFunction(safetyFunctionId)}
          className='inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground'
          aria-label={hazardLabel}
        >
          <ChevronRightIcon className='size-4' />
        </Link>
      </Item>
    </li>
  )
}

export function SafetyFunctionLinkedRiskReductionMeasuresSection({
  linkedRiskReductionMeasures,
  referenceHazardId,
  safetyFunctionId,
}: SafetyFunctionLinkedRiskReductionMeasuresSectionProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const naLabel = t('hazardsPage.hazardDetail.na')
  const hasLinkedMeasures = linkedRiskReductionMeasures.length > 0

  return (
    <section className='space-y-3'>
      <h3 className='text-base font-semibold'>
        {t('safetyFunctionsPage.sfDetail.linkedRiskReductionMeasures.title')}
      </h3>
      {!hasLinkedMeasures && (
        <p className='text-sm text-muted-foreground'>
          {t('safetyFunctionsPage.sfDetail.linkedRiskReductionMeasures.empty')}
        </p>
      )}
      {hasLinkedMeasures && (
        <ul className='space-y-3'>
          {linkedRiskReductionMeasures.map((measure) => {
            const measureTypeLabel = getMeasureTypeLabel(measure.type, t)
            const hasDescription =
              measure.description != null &&
              measure.description.trim().length > 0
            const hasLinkedHazards = measure.linkedHazards.length > 0
            let linkedHazardsHeading = t(
              'safetyFunctionsPage.sfDetail.linkedRiskReductionMeasures.linkedHazardsTitle'
            )
            if (hasLinkedHazards) {
              linkedHazardsHeading = t(
                'safetyFunctionsPage.sfDetail.linkedRiskReductionMeasures.linkedHazardsTitleWithCount',
                { count: measure.linkedHazards.length }
              )
            }

            return (
              <li
                key={measure.id}
                className='space-y-3 rounded-lg border border-border bg-muted/20 p-3'
              >
                <Item variant='outline' size='sm' className='bg-background'>
                  <ItemContent className='min-w-0 flex-1'>
                    <ItemTitle className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-foreground'>
                      <span className='min-w-0 truncate'>{measure.name}</span>
                      <Badge variant='outline' className='shrink-0 font-normal'>
                        {measureTypeLabel}
                      </Badge>
                    </ItemTitle>
                    {hasDescription && (
                      <LinkedEntityDescription
                        description={measure.description?.trim() ?? ''}
                      />
                    )}
                  </ItemContent>
                  <Link
                    relative='route'
                    to={`../risk-reduction-measures/${measure.id}`}
                    className='inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground'
                    aria-label={measure.name}
                  >
                    <ChevronRightIcon className='size-4' />
                  </Link>
                </Item>

                <div className='space-y-2 border-l-2 border-muted-foreground/25 ps-3'>
                  <h4 className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>
                    {linkedHazardsHeading}
                  </h4>
                  {!hasLinkedHazards && (
                    <p className='text-sm text-muted-foreground'>
                      {t(
                        'safetyFunctionsPage.sfDetail.linkedRiskReductionMeasures.noLinkedHazards'
                      )}
                    </p>
                  )}
                  {hasLinkedHazards && (
                    <ul className='space-y-1'>
                      {measure.linkedHazards.map((hazard) => (
                        <LinkedHazardRow
                          key={`${measure.id}-${hazard.id}`}
                          hazard={hazard}
                          referenceHazardId={referenceHazardId}
                          safetyFunctionId={safetyFunctionId}
                          naLabel={naLabel}
                          translate={t}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
